import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { EmptyState } from '../agents/EmptyState';
import { PendingShares } from './PendingShares';
import { RecencyBands } from './RecencyBands';
import { TasksFilterBar } from '../tasks/TasksFilterBar';
import { Card, Typography } from '../ui';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../lib/query-client';
import { useAgentTaskDuplexer } from '../../../lib/use-agent-task-duplexer';
import {
  recordAppOpen,
  useAllWorkspaceApps,
  usePendingAppShares,
  useSharedApps,
  type WorkspaceApp,
} from '../../../lib/app-shares';
import { appLinkTarget, listsHaveAnswered, openParam } from '../../../lib/app-open-target';
import { useWorkspaces } from '../../../lib/workspaces';
import { NO_TASK_FILTERS, type FilterOption } from '../../../lib/task-filters';
import { appsSubtitleKey } from '../../../lib/app-recency';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useMarkVisited } from '../../../lib/use-last-visited';
import browserStyles from '../files/FilesBrowser.module.css';
import appStyles from './WorkspaceAppsSection.module.css';
import { isHomeEmptyForced } from '../../../lib/dev-home-empty-states';

export const APPS_PAGE = 'apps';
const PAGE = APPS_PAGE;

// The filter is `TasksFilterBar` with the agent axis left empty, not a second bar built to look like it.
// Apps have no agent to narrow by (nothing records which one built it). The Space menu draws from one
// option, as every category does.
/**
 * `header` is a render-prop because the
 * page's subtitle COUNTS (see `appsSubtitleKey`) and only this body knows the count — and whether the
 * account has no apps at all, when `emptyState` (the home's designed one) replaces the plain empty card.
 */
export function AppsBody({
  header,
  emptyState,
}: {
  header?: (countedSubtitle: string | undefined, empty: boolean) => ReactNode;
  emptyState?: ReactNode;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  // Clears the Apps hub tile's "N New" badge — the reader has now seen the apps.
  useMarkVisited('apps');

  const workspacesState = useWorkspaces();
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  const workspaceIds = useMemo(() => workspaces.map((w) => w.id).filter(Boolean), [workspaces]);
  const state = useAllWorkspaceApps(workspaceIds);
  const queryClient = useQueryClient();
  // Refetch rather than splice the deleted app out of the cache: the server is the one that knows
  // what actually went, and a list edited locally after a partial failure would show the app gone
  // while it is still there on the next load.
  const refetchApps = () => queryClient.invalidateQueries({ queryKey: ['workspace-apps'] });
  // An app arrives here when a BUILD finishes, and nothing on this page knew that had happened: the
  // card only appeared if the user reloaded. The build's own task row is already pushed to this
  // browser over the duplexer, so the same signal that moves the task pill can refresh this list —
  // the app landing and the task closing are the same moment.
  useAgentTaskDuplexer(refetchApps);
  const sharedState = useSharedApps();
  const pendingState = usePendingAppShares();
  const [filters, setFilters] = useState(NO_TASK_FILTERS);

  const apps = state.status === 'ready' ? state.apps : [];

  // Still loading until BOTH the Space list and the apps in those Spaces have answered. `state` reports
  // ready-with-nothing the moment it is handed an empty id list, which is what it gets while the Spaces
  // are still in flight — so reading it alone says "no apps" about an account that has them.
  const loading = workspacesState.status === 'loading' || state.status === 'loading';
  // `failed` covers the reader's OWN apps only. Apps shared with them come from a separate read, so a
  // failure here must not take them down with it: folding shared apps into the stream made one 502 on
  // workspace-artifacts hide three perfectly-loaded shared apps behind "Couldn't load your apps", and
  // Retry only re-reads the half that was already broken. The error card is shown when the own-apps
  // read failed AND there is nothing else to show.
  const failed = workspacesState.status === 'error' || state.status === 'error';

  // Space id -> name for the card meta. Built from the same list the filter is built from, so a card
  // and the filter chip can never name the same Space differently.
  const spaceNames = useMemo(
    () => Object.fromEntries(workspaces.filter((w) => w.id).map((w) => [w.id, w.name])),
    [workspaces],
  );

  const spaceOptions: FilterOption[] = useMemo(() => {
    const owning = new Set(apps.map((a) => a.workspaceId).filter(Boolean));
    return workspaces.filter((w) => owning.has(w.id)).map((w) => ({ id: w.id, name: w.name }));
  }, [apps, workspaces]);

  // Apps somebody shared with the reader join the STREAM rather than sitting in a section of their
  // own. Recency is the question this screen answers, and a shared app you use every day belongs above
  // one of your own you have not touched in a month — the card still says whose it is. They carry no
  // workspaceId (they live in no Space of yours), so a Space-narrowed view drops them, which is right:
  // the filter is asking about YOUR Spaces.
  const sharedApps = sharedState.status === 'ready' ? sharedState.apps : [];
  const everything = [...apps, ...sharedApps];
  const forcedEmpty = !!emptyState && isHomeEmptyForced();
  const shown = forcedEmpty ? [] : filters.workspaceId ? everything.filter((a) => a.workspaceId === filters.workspaceId) : everything;
  /**
   * A count is a FACT ABOUT THE ACCOUNT, so it may only be stated once a read has actually supported
   * it. While loading, or when every Space failed, `shown` is empty for a reason that has nothing to do
   * with how many apps exist — and "0 apps your agents built" printed above a spinner or above
   * `apps.section.loadFailed` is a read failure shipped as an answer.
   */
  const empty = !loading && !failed && shown.length === 0;
  // The home's designed card also needs the shared and offered reads to have answered empty: an offer waiting to
  // be answered counts as something, and a failed read says nothing (iOS/Android `HomeCategoryEmptyState`).
  const designedEmpty =
    empty &&
    !!emptyState &&
    (forcedEmpty ||
      (sharedState.status === 'ready' && pendingState.status === 'ready' && pendingState.apps.length === 0));
  const subtitleKey = appsSubtitleKey(shown.length, loading);
  const countedSubtitle = subtitleKey ? t(subtitleKey, { count: shown.length }) : undefined;
  // The ordering is fed by opens, so an open has to invalidate the lists it reordered.
  const refetchAfterOpen = () => {
    void queryClient.invalidateQueries({ queryKey: ['workspace-apps'] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.sharedApps() });
  };

  useOpenSharedAppFromLink({
    owned: apps,
    shared: sharedApps,
    pending: pendingState.status === 'ready' ? pendingState.apps : [],
    // Every list must have ANSWERED before the id is resolved, and a failed read counts as answered:
    // the offers read in particular must not be skipped, or the shared-with-a-new-user link — the one
    // case this whole path exists for — resolves against a half-built picture and can open an app the
    // reader has not accepted yet.
    ready: listsHaveAnswered({
      // The SPACES read is in here for a reason: see `listsHaveAnswered`.
      workspaces: workspacesState.status,
      owned: state.status,
      shared: sharedState.status,
      pending: pendingState.status,
    }),
  });

  return (
    <>
        {/* The subtitle COUNTS: see `appsSubtitleKey`. */}
        {header?.(countedSubtitle, designedEmpty)}

        {!designedEmpty && spaceOptions.length > 0 && (
          <TasksFilterBar
            className={appStyles.filters}
            spaces={spaceOptions}
            agents={[]}
            filters={filters}
            onChange={setFilters}
            onBi={(name) => logPageClickBi({ page_name: PAGE, button_name: name })}
          />
        )}

        {/* Shares OFFERED to this user, ABOVE the apps they have — an offer is a thing to answer, and
            burying it under a grid of apps they already own is how it gets missed. Absent entirely when
            there are none, so the common case gains no empty box. */}
        {!forcedEmpty && pendingState.status === 'ready' && pendingState.apps.length > 0 && (
          <PendingShares
            offers={pendingState.apps}
            onBi={(name) => logPageClickBi({ page_name: PAGE, button_name: name })}
          />
        )}

        {/* Exactly ONE of these four renders. */}
        {loading && (
          <Card disablePadding className={browserStyles.card}>
            <div className={browserStyles.noResults}>{t('common.loading')}</div>
          </Card>
        )}

        {!loading && failed && shown.length === 0 && (
          <Card disablePadding className={browserStyles.card}>
            <div className={browserStyles.noResults}>{t('apps.section.loadFailed')}</div>
          </Card>
        )}

        {/* The illustrated card the rest of the app uses for "nothing here yet" (Figma `11246:167061`),
            not the bare loading/error text above — this is the one branch that's genuinely empty rather
            than still finding out. */}
        {empty && (designedEmpty ? emptyState : <EmptyState label={t('apps.section.empty')} />)}

        {!loading && shown.length > 0 && (
          <>
            <RecencyBands
              apps={shown}
              spaceNames={spaceNames}
              onOpenBi={() => logPageClickBi({ page_name: PAGE, button_name: 'open_app' })}
              onDeleted={refetchApps}
              onOpened={refetchAfterOpen}
            />
            {/* Some Spaces answered and some did not. Saying nothing would show a shorter list that looks
                complete, and nothing on screen would tell the two apart. */}
            {state.status === 'ready' && state.failedSpaces > 0 && (
              <Typography variant="p3" color="secondary" component="p">
                {t('apps.section.partial')}
              </Typography>
            )}
          </>
        )}

        {/* The own-apps failure, when there IS still a stream to show (shared apps loaded fine). */}
        {!loading && failed && shown.length > 0 && (
          <Typography variant="p3" color="secondary" component="p">
            {t('apps.section.loadFailed')}
          </Typography>
        )}

        {/* Shared apps are IN the stream above, so only a failed read of them is said here. */}
        {/* Its OWN sentence, not the owned-list one. This sits UNDER a grid of apps that loaded
            fine, and "Couldn't load your apps" there contradicts what the user can see and sends
            them looking for a failure that did not happen. */}
        {sharedState.status === 'error' && (
          <Typography variant="p3" color="secondary" component="p">
            {t('apps.sharedWithMe.loadFailed')}
          </Typography>
        )}
    </>
  );
}



/**
 * `/apps?open=<appId>` — the tail of the share link `symphony.wix.com/app/<appId>`.
 *
 * Runs the same rule both native surfaces run (`resolvedAppToOpen`): the reader is already ON the Apps
 * screen by the time this fires, and the id either names something they can open or it does not. An
 * unanswered offer resolves to nothing ON PURPOSE — it is sitting at the top of the screen they just
 * landed on, and accepting it is the decision the link exists to ask for.
 *
 * <p><b>Same tab, not a new one.</b> `AppsGrid` opens a card in a new tab because that is a click on a
 * card inside a screen the reader means to stay on. This is a LINK the reader followed to get here, so
 * the current tab is where it belongs — and a programmatic `window.open` after an await is popup-blocked
 * anyway, which would have made the link silently do nothing for a first-time visitor. Assigning
 * `location` hands the app no `window.opener`, so third-party app code still gets no handle on Symphony.
 *
 * <p>The param is stripped either way, so a refresh (or a Back into this entry) does not re-open, and
 * the latch keeps a re-render from firing it twice while the lists settle.
 */
function useOpenSharedAppFromLink({
  owned,
  shared,
  pending,
  ready,
}: {
  owned: WorkspaceApp[];
  shared: WorkspaceApp[];
  pending: WorkspaceApp[];
  ready: boolean;
}) {
  const router = useRouter();
  const handled = useRef(false);

  useEffect(() => {
    if (!router.isReady || handled.current) return;
    const appId = openParam(router.query.open);
    if (!appId) return;
    if (!ready) return;
    handled.current = true;
    const target = appLinkTarget(appId, owned, shared, pending);
    // A HOSTED app (owned, shared or only offered) goes to its host page, which answers an offer inline before
    // it opens. `replace`, so Back from the app lands on the Apps screen rather than bouncing through `?open=`.
    if (target?.kind === 'host') {
      void router.replace(target.path);
      return;
    }
    // Cleared before navigating, so coming Back lands on a plain Apps screen rather than re-opening.
    void router.replace('/apps', undefined, { shallow: true });
    if (!target?.app.url) return;
    // Not awaited, and its failure is swallowed — the same bookkeeping rule the card's own tap uses.
    void recordAppOpen(target.app.id);
    window.location.href = target.app.url;
  }, [router, router.isReady, router.query.open, ready, owned, shared, pending]);
}
